import React, { ChangeEvent, CSSProperties, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import AnswerOptionList from '../answer-option/AnswerOptionList';
import { Button } from '../../../../../common/button/Button';
import colors from '../../../../../../../styles/colors';
import { Add, Delete } from '@material-ui/icons';
import {
  FormHelperText,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import CustomInput from '../../../../../common/input/CustomInput';
import Editor from '../../../../../common/editor/Editor';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import AssignmentQuestionFormatSelect from '../question-format/AssignmentQuestionFormatSelect';
import {
  AssignmentIndependentQuestion,
  AssignmentQuestion,
  AssignmentQuestionFormat
} from '../../../state/assignment-edit.types';
import { useAppSelector } from '../../../../../../../redux/hooks';
import { QuestionFormat } from '../../../../../../../enums/QuestionFormat';
import { validateQuestion } from '../../../../../util/AssignmentValidation';

const useStyles = makeStyles({
  delete: {
    textAlign: 'right'
  }
});

interface AssignmentQuestionEditorProps {
  showQuestionFormat?: boolean;
  question: AssignmentIndependentQuestion;
  handleDeleteQuestion(id: string): void;
  handleAnswerUpdate(id: string, answerId: string, data: object): void;
  handleAddQuestionAnswer(id: string): void;
  handleDeleteAnswer(questionId: string, answerId: string): void;
  handleQuestionFormat(id: string, format: AssignmentQuestionFormat): void;
  handleTextChange(id: string, data: object): void;
  handleQuestionIndex(id: string): number;
  index?: number;
  style?: CSSProperties;
}

const AssignmentQuestionEditor: React.FC<any> = (
  props: AssignmentQuestionEditorProps
) => {
  const classes = useStyles();
  const {
    showQuestionFormat,
    handleQuestionFormat,
    handleTextChange,
    handleAnswerUpdate,
    handleDeleteAnswer,
    handleAddQuestionAnswer,
    handleQuestionIndex,
    handleDeleteQuestion,
    question,
    style,
    index: rootIndex
  } = props;
  const { format, id: questionId, answers, skillId, error } = question;
  const skills = useAppSelector(state => state.editAssignment.skills);
  const questionTypes = useAppSelector(
    state => state.editAssignment.questionTypes
  );
  const handleChange = (evt: ChangeEvent<any>) => {
    if (questionId) {
      handleTextChange(questionId, { [evt.target.name]: evt.target.value });
    }
  };

  const handleEditorChange = (data: string) => {
    if (questionId) {
      handleTextChange(questionId, { question: data });
    }
  };

  const handleQuestionAnswerUpdate = (id: string, data: object) => {
    handleAnswerUpdate(questionId, id, data);
  };

  const handleQuestionDeleteAnswer = (answerId: string) => {
    handleDeleteAnswer(questionId, answerId);
  };

  const index = handleQuestionIndex(questionId);
  const questionType = questionTypes.find(
    questionType => questionType.id === format
  );
  /**
   * Circumvent error where errors property is turned into object
   */
  if (!(error?.errors instanceof Map)) {
    if (error) {
      error.errors = new Map();
    }
  }

  return (
    <Grid container spacing={2} style={{ ...style }}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {rootIndex ? `Passage ${rootIndex}-${index}` : `Question ${index}`}
        </Typography>
      </Grid>
      {showQuestionFormat && (
        <Grid item xs={12} sm={4}>
          <AssignmentQuestionFormatSelect
            questionTypes={questionTypes.filter(
              (questionType: any) => questionType.id !== 3
            )}
            format={format ? format : -1}
            handleAssignmentEdit={handleQuestionFormat}
            id={questionId}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Editor
          placeholder={'Question...'}
          value={question.question}
          handleChange={handleEditorChange}
        />
        {error?.errors?.get('question') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('question')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <AnswerOptionList
          isOrdered={questionType?.name === QuestionFormat.DragAndDrop
            || questionType?.name === QuestionFormat.Order}
          isPair={questionType?.name === QuestionFormat.Pair}
          handleAnswerUpdate={handleQuestionAnswerUpdate}
          handleDeleteAnswer={handleQuestionDeleteAnswer}
          answers={answers}
        />
        {error?.errors?.get('answers') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('answers')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              color={colors.assignment.grey}
              icon={Add}
              onClick={() => handleAddQuestionAnswer(questionId)}
              variant={'outline'}
              disabled={questionType.name === QuestionFormat.TrueFalse && answers.length >= 2 ? true : false}
            >
              Add answer option
            </Button>
          </Grid>
          {/*{!showFeedback && (*/}
          {/*  <Grid item>*/}
          {/*    <Button*/}
          {/*      onClick={() => setShowFeedBack(true)}*/}
          {/*      color={colors.assignment.grey}*/}
          {/*      icon={Add}*/}
          {/*      variant={'outline'}*/}
          {/*    >*/}
          {/*      Feedback*/}
          {/*    </Button>*/}
          {/*  </Grid>*/}
          {/*)}*/}
        </Grid>
      </Grid>
      {/*{showFeedback && (*/}
      {/*  <Grid item xs={12}>*/}
      {/*    <CustomInput*/}
      {/*      name={'feedbackIncorrect'}*/}
      {/*      onChange={handleChange}*/}
      {/*      value={feedbackIncorrect}*/}
      {/*      multiline={true}*/}
      {/*      placeholder={'Feedback...'}*/}
      {/*    />*/}
      {/*  </Grid>*/}
      {/*)}*/}
      <Grid item xs={12}>
        <Select
          fullWidth={true}
          label={'Skill'}
          name={'skillId'}
          required={true}
          value={skillId || -1}
          onChange={handleChange}
          input={<CustomInput />}
        >
          <MenuItem value={-1}>-- Select Skill --</MenuItem>
          {skills.map(skill => {
            return (
              <MenuItem key={skill.id} value={skill.id}>
                {skill.name}
              </MenuItem>
            );
          })}
        </Select>
        {error?.errors?.get('skillId') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('skillId')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12} className={classes.delete}>
        <IconButton
          title="Remove"
          onClick={() => handleDeleteQuestion(questionId)}
        >
          <Delete />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default AssignmentQuestionEditor;
